import React, { useEffect, Suspense, useMemo } from 'react';
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useParams,
  useLocation,
} from 'react-router-dom';
import { withRouter } from '@lib/router';
import { TopProgressBar, lazy } from '@components/common/routeLoading';
import { requiresAuth, requiresMfaSession } from '@auth/appRouter/requiresAuth';
import { disableCognito } from '@config';
import useStores from '@stores/useStores';
import { observer } from 'mobx-react';
import { RESOURCE_TYPES, SESSION_STORAGE_KEYS } from '@components/common/constants';
import { RudderCategory } from '@components/common/types';
import { apiAuthCaller } from '@services/apiCaller';
import { Path } from '@components/routes/constants';
import { authorizedRoutes } from '@components/routes/authorizedRoutes';
import { RouteBoundary } from '@components/routes/routeBoundary';
import { RedirectRoute } from '@components/routes/redirect';

import { OauthRedirect } from './oauthRedirect';
import MetaTag from './metaTag';
import { Toast } from './toast';
import { RouteErrorBoundary } from './routeErrorBoundary';
import { useFeatureFlagObserver } from '@hooks/useFeatureFlagObserver';
import { useDocumentTitle } from '@hooks/useDocumentTitle';
import { RouteEntityNameProvider } from '@hooks/useDocumentTitle/entityName';
import { SECTION } from '@hooks/useDocumentTitle/constants';
import { usePageTracking } from '@hooks/usePageTracking';
import { useCustomerIoInApp } from '@hooks/useCustomerIoInApp';
import { PageContextProvider } from '@context/pageContext';
import { FeatureFlagKeys } from '@lib/experiment';

const ForgotPasswordPage = lazy(
  () => import(/* webpackChunkName: "auth.forgotPassword" */ '@auth/forgotPassword'),
);
const SSOPage = lazy(() => import(/* webpackChunkName: "auth.sso" */ '@auth/sso'));
const McpAuthorizationPage = lazy(() =>
  import(/* webpackChunkName: "auth.authorize" */ '@components/mcpAuthorizationPage').then((m) => ({
    default: m.McpAuthorizationPage,
  })),
);
const ESignupPage = lazy(
  () => import(/* webpackChunkName: "auth.esignup" */ '@auth/signup/esignup'),
);
const EsignupV2Page = lazy(() =>
  import(/* webpackChunkName: "auth.esignupV2" */ '@authV2/esignup').then((m) => ({
    default: m.EsignupV2,
  })),
);
const Home = lazy(() => import(/* webpackChunkName: "home" */ '@components/home/home'));
const AcceptInvite = lazy(
  () => import(/* webpackChunkName: "auth.acceptInvite" */ '@auth/acceptInvite'),
);
const LoginPage = lazy(() => import(/* webpackChunkName: "auth.login" */ '@auth/login/login'));
const BlendoSignupPage = lazy(
  () => import(/* webpackChunkName: "auth.blendoSignup" */ '@auth/blendo/blendoSignup'),
);
const MfaVerify = lazy(
  () => import(/* webpackChunkName: "auth.mfaVerify" */ '@auth/mfa/mfaVerify'),
);
const AddMfa = lazy(() => import(/* webpackChunkName: "auth.addMfa" */ '@auth/mfa/addMfa'));
const AddMfaLater = lazy(
  () => import(/* webpackChunkName: "auth.addMfaLater" */ '@auth/mfa/addMfaLater'),
);
const AddMfaSuccess = lazy(
  () => import(/* webpackChunkName: "auth.addMfaSuccess" */ '@auth/mfa/addMfaSuccess'),
);
const AddPhonePage = lazy(
  () => import(/* webpackChunkName: "auth.addPhonePage" */ '@auth/addPhonePage'),
);
const TotpVerify = lazy(() =>
  import(/* webpackChunkName: "auth.totpVerify" */ '@auth/mfa/totpVerify').then((m) => ({
    default: m.MfaVerifyPage,
  })),
);
const TotpSetup = lazy(() =>
  import(/* webpackChunkName: "auth.totpSetup" */ '@auth/mfa/totpSetup').then((m) => ({
    default: m.TotpSetupPage,
  })),
);
const Signup = lazy(() => import(/* webpackChunkName: "auth.signup" */ '@auth/signup'));
const SignupRedirectPage = lazy(() =>
  import(/* webpackChunkName: "auth.signupRedirect" */ '@auth/signupRedirect').then((m) => ({
    default: m.SignupRedirect,
  })),
);
const SetupAccountV2Flow = lazy(() =>
  import(/* webpackChunkName: "auth.setupAccountV2" */ '@authV2/setupAccountV2').then((m) => ({
    default: m.SetupAccountV2,
  })),
);
const SetupAccountOriginal = lazy(() =>
  import(
    /* webpackChunkName: "auth.setupAccountOriginal" */ '@auth/setupAccount/setupAccount'
  ).then((m) => ({
    default: m.SetupAccountOriginal,
  })),
);
const Security = lazy(() => import(/* webpackChunkName: "security" */ '@components/security'));
const WaitingAuthentication = lazy(
  () => import(/* webpackChunkName: "auth.WaitingAuthentication" */ './WaitingAuthentication'),
);
const SelectOrganization = lazy(
  () => import(/* webpackChunkName: "organizations" */ '@components/organizations'),
);
const UserSuspendedInOrganization = lazy(() =>
  import(
    /* webpackChunkName: "organizations.suspended" */ '@components/organizations/suspended'
  ).then((m) => ({
    default: m.UserSuspendedInOrganization,
  })),
);

// Route guards are stable module-level components so v6 `element={<X />}` does not
// remount them on every render of AppRouter.
const GuardedAddMfa = requiresMfaSession(AddMfa);
const GuardedAddMfaLater = requiresAuth(AddMfaLater);
const GuardedAddMfaSuccess = requiresMfaSession(AddMfaSuccess);
const GuardedAddPhone = requiresMfaSession(AddPhonePage);
const GuardedVerifyPhone = requiresMfaSession(MfaVerify);
const GuardedVerifyTotp = requiresMfaSession(TotpVerify);
const GuardedSetupTotp = requiresMfaSession(TotpSetup);
const GuardedUserSuspended = requiresAuth(UserSuspendedInOrganization);
const GuardedSelectOrganization = requiresAuth(SelectOrganization);
const GuardedHome = requiresAuth(Home);

// Legacy page components that read router props (history/location/match) via the old
// `component={}` injection; withRouter supplies those under v6's `element={}` model.
const ForgotPassword = withRouter(ForgotPasswordPage) as unknown as React.FC;
const EsignupV1Page = withRouter(ESignupPage) as unknown as React.FC;
const EsignupV2Wrapped = withRouter(EsignupV2Page) as unknown as React.FC;
const SignupComponent = disableCognito
  ? EsignupV1Page
  : (withRouter(Signup) as unknown as React.FC);
const GuardedSetupAccountV1 = requiresAuth(SetupAccountOriginal);
const GuardedSetupAccountV2 = requiresAuth(SetupAccountV2Flow);
const enableMFA = !disableCognito;

/**
 * Route-level switches for the PLG onboarding v2 flag. The flag resolves
 * asynchronously after mount, so the branching must live INSIDE a stable route
 * element: the router (and its history listeners) is created exactly once,
 * while the rendered page swaps when the flag flips.
 */
const EsignupSwitch: React.FC = () => {
  const isPlgOnboardingV2Enabled = useFeatureFlagObserver(FeatureFlagKeys.enablePlgOnboardingV2);
  // Only the V2 form creates the account without an organization, so the split flag picks it
  // too. Without this, turning the split on changes nothing wherever V2 is still off.
  const isOrganizationSetUpLater = useFeatureFlagObserver(FeatureFlagKeys.enableOrganizationSetup);
  return isPlgOnboardingV2Enabled || isOrganizationSetUpLater ? (
    <EsignupV2Wrapped />
  ) : (
    <EsignupV1Page />
  );
};

const SetupAccountSwitch: React.FC = () => {
  const isPlgOnboardingV2Enabled = useFeatureFlagObserver(FeatureFlagKeys.enablePlgOnboardingV2);
  return isPlgOnboardingV2Enabled ? <GuardedSetupAccountV2 /> : <GuardedSetupAccountV1 />;
};

/** Keeps the browser tab title in sync with SPA navigation and loaded resource names. */
const DocumentTitle = observer(() => {
  useDocumentTitle();
  return null;
});

/** Fires analytics `page` events on SPA navigation. Mounted inside <Router>; renders nothing. */
const RouteTracker = () => {
  usePageTracking();
  return null;
};

/** Keeps Customer.io in-app messaging on the current theme and rollout flag. Renders nothing. */
const CustomerIoInAppSync = () => {
  useCustomerIoInApp();
  return null;
};

const OAuthCallbackPage: React.FC = () => {
  const { userStore } = useStores();
  const params = useParams<{ role: string; rudderCategory?: RudderCategory }>();
  const location = useLocation();

  useEffect(() => {
    const oauthCallback = async () => {
      const isDest = params.rudderCategory === RESOURCE_TYPES.DESTINATION;
      const oauthMetaKey = isDest ? SESSION_STORAGE_KEYS.destination : SESSION_STORAGE_KEYS.source;

      const { pathname, search } = location;
      const OauthMetadata = sessionStorage.getItem(oauthMetaKey);

      let extraQueryParams = {
        t: window.btoa(userStore.token),
        workspaceId: userStore?.selectedWorkspaceId,
      };

      if (OauthMetadata) {
        try {
          const metadataFromStorage = JSON.parse(OauthMetadata);
          extraQueryParams = {
            ...metadataFromStorage,
            ...extraQueryParams,
          };
        } catch (err) {}
      }

      const urlData = await apiAuthCaller({ nonWorkspaceRoute: true }).get(`${pathname}${search}`, {
        params: extraQueryParams,
      });

      window.location.assign(urlData.data);
    };

    oauthCallback();
  }, []);

  return <WaitingAuthentication role={params.role} />;
};

/**
 * Root layout route: renders the always-present chrome (meta tags, page tracker)
 * and the app-wide Suspense boundary, with the matched route rendered through
 * <Outlet />. Mounted once at the top of the data-router tree so it persists
 * across navigation.
 *
 * The boundary is <RouteBoundary />, which keys on the match directly below it
 * rather than on the matched leaf — see @components/routes/routeBoundary for why
 * (a leaf key here would remount the whole app shell on every navigation).
 */
const RootLayout: React.FC = () => (
  // The entity-name provider wraps both the title host and the outlet: pages
  // below publish the name of what they are showing, and DocumentTitle — the
  // single place that writes document.title — composes it with the route's
  // static handle metadata.
  <RouteEntityNameProvider>
    <PageContextProvider>
      <TopProgressBar />
      <MetaTag />
      <DocumentTitle />
      <RouteTracker />
      <CustomerIoInAppSync />
      <RouteBoundary />
    </PageContextProvider>
  </RouteEntityNameProvider>
);

/**
 * The full route tree. Exported so tests can walk exactly the definitions the
 * app runs, rather than a copy that can drift from them.
 */
// ts-prune-ignore-next -- consumed by __tests__, which are outside the deadcode project
export const appRoutes = (
  <Route element={<RootLayout />} errorElement={<RouteErrorBoundary />}>
    {/* `/*` restores v5 prefix matching so /health/live also resolves here */}
    <Route
      path="/health/*"
      element={<h3>Hey There!!! The App is Healthy</h3>}
      handle={{ section: SECTION.health }}
    />
    <Route
      path="/authorize"
      element={<McpAuthorizationPage />}
      handle={{ section: SECTION.authorize }}
    />
    <Route
      path="/blendoSignup"
      element={<BlendoSignupPage />}
      handle={{ section: SECTION.signUp }}
    />
    <Route path={Path.SIGNUP} element={<SignupComponent />} handle={{ section: SECTION.signUp }} />
    <Route path={Path.LOGIN} element={<LoginPage />} handle={{ section: SECTION.logIn }} />
    <Route path="/esignup" element={<EsignupSwitch />} handle={{ section: SECTION.signUp }} />
    <Route
      path={Path.SIGNUP_REDIRECT}
      element={<SignupRedirectPage />}
      handle={{ section: SECTION.signUp }}
    />
    <Route path="/elogin" element={<LoginPage />} handle={{ section: SECTION.logIn }} />
    <Route path="/sso" element={<SSOPage />} handle={{ section: SECTION.singleSignOn }} />
    {/* The OAuth redirect and the provider callbacks are one flow, so
          they share a section — the callbacks used to read "Authentication". */}
    <Route path="/oauth" element={<OauthRedirect />} handle={{ section: SECTION.accountLinking }} />
    <Route
      path="/auth/:role/callback"
      element={<OAuthCallbackPage />}
      handle={{ section: SECTION.accountLinking }}
    />
    <Route
      path="/auth/:rudderCategory/:role/callback"
      element={<OAuthCallbackPage />}
      handle={{ section: SECTION.accountLinking }}
    />
    <Route
      path="/oauth/success/:id"
      element={<WaitingAuthentication />}
      handle={{ section: SECTION.accountLinking }}
    />
    <Route
      path="/oauth/error/:message"
      element={<WaitingAuthentication />}
      handle={{ section: SECTION.accountLinking }}
    />
    <Route
      path="/forgotPassword"
      element={<ForgotPassword />}
      handle={{ section: SECTION.forgotPassword }}
    />
    <Route
      path="/acceptInvite"
      element={<AcceptInvite />}
      handle={{ section: SECTION.acceptInvite }}
    />
    {enableMFA && (
      <Route
        path="/addmfa"
        element={<GuardedAddMfa />}
        handle={{ section: SECTION.multiFactorAuthentication }}
      />
    )}
    {enableMFA && (
      <Route
        path="/addmfalater"
        element={<GuardedAddMfaLater />}
        handle={{ section: SECTION.multiFactorAuthentication }}
      />
    )}
    {enableMFA && (
      <Route
        path="/addmfasuccess"
        element={<GuardedAddMfaSuccess />}
        handle={{ section: SECTION.multiFactorAuthentication }}
      />
    )}
    {enableMFA && (
      <Route
        path="/addphone"
        element={<GuardedAddPhone />}
        handle={{ section: SECTION.phoneVerification }}
      />
    )}
    {enableMFA && (
      <Route
        path="/verifyphone"
        element={<GuardedVerifyPhone />}
        handle={{ section: SECTION.phoneVerification }}
      />
    )}
    {enableMFA && (
      <Route
        path="/verify-totp"
        element={<GuardedVerifyTotp />}
        handle={{ section: SECTION.multiFactorAuthentication }}
      />
    )}
    {enableMFA && (
      <Route
        path="/setup-totp"
        element={<GuardedSetupTotp />}
        handle={{ section: SECTION.multiFactorAuthentication }}
      />
    )}
    <Route
      path={Path.ACCOUNT_SETUP}
      element={<SetupAccountSwitch />}
      handle={{ section: SECTION.accountSetup }}
    />
    <Route
      path={Path.USER_SUSPENDED}
      element={<GuardedUserSuspended />}
      handle={{ section: SECTION.userSuspended }}
    />
    <Route
      path={`${Path.SELECT_ORGANIZATION}/*`}
      element={<GuardedSelectOrganization />}
      handle={{ section: SECTION.selectOrganization }}
    />
    {/*
     * Authenticated app shell as a LAYOUT route: GuardedHome renders the
     * chrome (sidebar, header, modals) and an <Outlet />, so every page
     * below is matched by the data router itself. That is what makes route
     * `handle` metadata reachable from useMatches() in RootLayout — a page
     * rendered from a descendant <Routes> block is invisible to it and
     * would silently return no metadata.
     *
     * Pathless on purpose: react-router forbids children under a splat
     * (`/*`) path, so the layout contributes no path segment and its
     * children carry absolute paths.
     *
     * The trailing `*` is the app's final fallback for a signed-in user;
     * feature sections declare their own not-found redirects above it.
     */}
    <Route element={<GuardedHome />}>
      {authorizedRoutes}
      <Route path="*" element={<RedirectRoute />} />
    </Route>
  </Route>
);

const AppRouter: React.FC = () => {
  const { sourceDefinitionsListStore, destinationDefsListStore, accountDefinitionsListStore } =
    useStores();

  useEffect(() => {
    const definitionApiCalls = [
      sourceDefinitionsListStore.getAll(),
      destinationDefsListStore.getDestinationDefs(),
      accountDefinitionsListStore.getAccountDefinitions(),
    ];

    Promise.all(definitionApiCalls);
  }, []);

  // The router MUST be created exactly once per AppRouter mount (empty dep
  // array): recreating it leaks the old router's popstate/pagehide listeners
  // (RouterProvider never disposes it) and tears the rendered state. All
  // async/conditional branching lives inside stable route elements above.
  const router = useMemo(
    () =>
      createBrowserRouter(createRoutesFromElements(appRoutes), {
        basename: process.env.PUBLIC_URL || '/',
      }),
    [],
  );

  return (
    <>
      <Toast />
      <Suspense fallback={null}>
        <Security />
      </Suspense>
      {/* react-router v7 wraps router state updates in React.startTransition by
          default (the old v6 `future.v7_startTransition` opt-in). Note that does
          NOT fix the APL-446 stuck-skeleton hang on its own — the keyed Suspense
          boundary in RootLayout does; see __tests__/lazyRouteNavigation.test.tsx. */}
      <RouterProvider router={router} />
    </>
  );
};

/* eslint-disable import/no-default-export */
export default observer(AppRouter);
